import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import programService from '@/shared/services/programService';

const ROLE_NAMES = { la: 'LAs', si: 'SI Leaders', ta: 'TAs' };
const whoOptions = (programType) => [
    { value: 'new', label: `New ${ROLE_NAMES[programType]}` },
    { value: 'returning', label: `Returning ${ROLE_NAMES[programType]}` },
    { value: 'all', label: 'Everyone' },
];

const BLANK = { title: '', when: '', appliesTo: 'new' };

/**
 * The dated trainings a program asks people to attend in one term, such as an
 * orientation or a seminar. Students and faculty see them before they apply.
 * The program keeps one list across terms; this card edits the rows for the
 * given term and sends the other terms' rows back unchanged. Each change is
 * saved as it is made, and an empty list means "no dates".
 */
function TrainingDatesCard({ programType, termCode, termName }) {
    const options = whoOptions(programType);
    const whoLabel = (value) =>
        options.find((o) => o.value === value)?.label || 'Everyone';
    const [all, setAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState('');
    const [saving, setSaving] = useState(false);
    // editing: null | { index, draft } where index -1 is a new session
    const [editing, setEditing] = useState(null);
    const [removing, setRemoving] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const config = await programService
                .getConfig(programType)
                .catch((err) => {
                    if (err.response?.status === 404) return {};
                    throw err;
                });
            setAll(config.trainingSessions || []);
        } catch (_err) {
            setLoadError('Failed to load the training dates.');
        } finally {
            setLoading(false);
        }
    }, [programType]);

    useEffect(() => {
        load();
    }, [load]);

    const mine = all
        .map((session, index) => ({ session, index }))
        .filter(({ session }) => session.term === termCode);

    const persist = async (next, message) => {
        setSaving(true);
        setSaveError(null);
        setNotice('');
        try {
            const config = await programService.upsertConfig(programType, {
                trainingSessions: next,
            });
            setAll(config?.trainingSessions ?? next);
            setNotice(message);
            return true;
        } catch (err) {
            setSaveError(
                err.response?.data?.message ||
                    'Failed to save the training dates.'
            );
            return false;
        } finally {
            setSaving(false);
        }
    };

    const draft = editing?.draft;
    const missing =
        draft && (draft.title.trim() === '' || draft.when.trim() === '');

    const saveDraft = async (event) => {
        event.preventDefault();
        if (missing || saving) return;
        const row = {
            term: termCode,
            title: draft.title.trim(),
            when: draft.when.trim(),
            appliesTo: draft.appliesTo,
        };
        const next =
            editing.index === -1
                ? [...all, row]
                : all.map((s, i) => (i === editing.index ? row : s));
        if (await persist(next, `Saved ${row.title}.`)) setEditing(null);
    };

    const confirmRemove = async () => {
        const { index, session } = removing;
        const ok = await persist(
            all.filter((_s, i) => i !== index),
            `Removed ${session.title}.`
        );
        if (ok) setRemoving(null);
    };

    const setDraft = (patch) =>
        setEditing((e) => ({ ...e, draft: { ...e.draft, ...patch } }));

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading the training dates...
            </div>
        );
    }
    if (loadError) return <Alert variant="danger">{loadError}</Alert>;

    return (
        <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
                <h2 className="h5">Training dates</h2>
                <p className="text-body-secondary">
                    Students see these before they apply. Write when in any
                    form, for example Fridays, 10:00-11:30 AM or Jan 12-13, 9
                    AM-3 PM.
                </p>
                {!termCode ? (
                    <p className="mb-0">
                        Choose a term to set its training dates.
                    </p>
                ) : (
                    <>
                        <h3 className="h6">
                            {termName ? `For ${termName}` : 'For this term'}
                        </h3>
                        {mine.length === 0 ? (
                            <p className="text-body-secondary">
                                No training dates yet. Students will not see a
                                training line until you add one.
                            </p>
                        ) : (
                            <ul className="list-unstyled mb-3">
                                {mine.map(({ session, index }) => (
                                    <li
                                        key={`${index}-${session.title}`}
                                        className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom py-2"
                                    >
                                        <div>
                                            <div className="fw-semibold">
                                                {session.title}
                                            </div>
                                            <div>{session.when}</div>
                                            <div className="text-body-secondary small">
                                                {whoLabel(session.appliesTo)}
                                            </div>
                                        </div>
                                        <div className="psh-button-pair">
                                            <Button
                                                variant="outline-primary"
                                                aria-label={`Edit ${session.title}`}
                                                onClick={() =>
                                                    setEditing({
                                                        index,
                                                        draft: {
                                                            title: session.title,
                                                            when: session.when,
                                                            appliesTo:
                                                                session.appliesTo ||
                                                                'all',
                                                        },
                                                    })
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                aria-label={`Remove ${session.title}`}
                                                onClick={() => {
                                                    setSaveError(null);
                                                    setRemoving({
                                                        index,
                                                        session,
                                                    });
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {saveError && !editing && !removing && (
                            <Alert variant="danger" role="alert">
                                {saveError}
                            </Alert>
                        )}
                        <div role="status">{notice}</div>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setSaveError(null);
                                setEditing({ index: -1, draft: { ...BLANK } });
                            }}
                        >
                            Add a session
                        </Button>
                    </>
                )}
            </Card.Body>

            <Modal show={!!editing} onHide={() => setEditing(null)} centered>
                <Form onSubmit={saveDraft} noValidate>
                    <Modal.Header closeButton>
                        <Modal.Title as="h2" className="h5">
                            {editing?.index === -1
                                ? 'Add a training session'
                                : 'Edit training session'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group controlId="training-title" className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={120}
                                placeholder="Orientation"
                                value={draft?.title || ''}
                                onChange={(e) =>
                                    setDraft({ title: e.target.value })
                                }
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group controlId="training-when" className="mb-3">
                            <Form.Label>When</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={200}
                                placeholder="Jan 12-13, 9 AM-3 PM"
                                value={draft?.when || ''}
                                onChange={(e) =>
                                    setDraft({ when: e.target.value })
                                }
                            />
                        </Form.Group>
                        <Form.Group controlId="training-who">
                            <Form.Label>Who attends</Form.Label>
                            <Form.Select
                                value={draft?.appliesTo || 'new'}
                                onChange={(e) =>
                                    setDraft({ appliesTo: e.target.value })
                                }
                            >
                                {options.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {saveError && (
                            <Alert
                                variant="danger"
                                role="alert"
                                className="mt-3 mb-0"
                            >
                                {saveError}
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-primary"
                            onClick={() => setEditing(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={saving || missing}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={!!removing} onHide={() => setRemoving(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5">
                        Remove this session?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className={saveError ? '' : 'mb-0'}>
                        {removing?.session.title} will no longer show to
                        students or faculty.
                    </p>
                    {saveError && (
                        <Alert variant="danger" role="alert" className="mb-0">
                            {saveError}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setRemoving(null)}
                    >
                        Keep it
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmRemove}
                        disabled={saving}
                    >
                        {saving ? 'Removing...' : 'Remove'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
}

export default TrainingDatesCard;
